import { Row, Col, Typography, Divider, Space, Tooltip, Table } from "antd";
import {
    formatCurrency,
    formatDate,
    capitalizeWords,
    calculateRelativeTime,
} from "../../../helpers/helpers";
import {
    BTN_TXT,
    GLOBAL_CONSTANTS,
    LABEL_TXT,
} from "../../../constants/constants";
import DealDetailNoSpace from "../../../components/Details/DealDetailNoSpace";
import DealDetail from "../../../components/Details/DealDetail";
import { isSupplyChainCategory } from "../../../helpers/dealCategoryProducts";

const { Text } = Typography;

const DealDetails = ({ deal }) => {
    const isSupplyChain = isSupplyChainCategory(deal?.dealCategoryId || deal?.dealType);

    return (
        <div className="deal-details-card-container">
            <div className="card-header-container">
                <Space direction="vertical" size={10}>
                    <DealDetailNoSpace
                        label={LABEL_TXT.BUS_NAME}
                        value={deal?.businessName}
                        recourseTag={<Text className="recourse-on-tag">Recourse on</Text>}
                    />
                    <DealDetailNoSpace
                        label={LABEL_TXT.ENT_NAME}
                        value={deal?.enterpriseName}
                    />
                </Space>
                <Space direction="vertical" size={10}>
                    <DealDetailNoSpace
                        label={LABEL_TXT.DEAL_STATUS}
                        value={
                            <Text className="deal-status-tag">
                                {capitalizeWords(deal?.dealStatus)}
                            </Text>
                        }
                    />
                    <DealDetailNoSpace label={LABEL_TXT.DEAL_ID} value={deal?.dealId} />
                </Space>
            </div>

            <Row className="details-row-container" gutter={[0, 0]}>
                <Col xs={24} md={12} className="details-equal-col">
                    <div className="details-col-container">
                        <DealDetail
                            label="Deal Type"
                            value={capitalizeWords(deal?.dealType)}
                        />
                        <DealDetail
                            label="Deal Visibility"
                            value={capitalizeWords(deal?.dealVisibility)}
                        />
                    </div>
                </Col>

                <Col
                    xs={0}
                    md={1}
                    style={{ display: "flex", justifyContent: "center" }}
                >
                    <Divider
                        type="vertical"
                        style={{
                            height: "100%",
                            borderLeft: "1px solid #CBD5E2",
                            margin: 0,
                        }}
                    />
                </Col>

                <Col xs={24} md={11} className="details-equal-col">
                    <div className="details-col-container">
                        <DealDetail
                            label="Line of Business"
                            value={capitalizeWords(deal?.lineOfBusiness)}
                        />
                        <DealDetail
                            label="History with Karncy"
                            value={
                                <Tooltip
                                    title={`For ${calculateRelativeTime(
                                        deal?.busCreatedAt
                                    )}, this business has actively utilized the Karncy platform to create value and drive impact.`}
                                >
                                    <Text style={{ textDecoration: "underline" }}>
                                        {BTN_TXT.VIEW}
                                    </Text>
                                </Tooltip>
                            }
                        />
                    </div>
                </Col>
            </Row>

            <Divider style={{ border: "1px solid #CBD5E2", margin: 0 }} />

            <Row className="details-row-container" gutter={[0, 0]}>
                <Col xs={24} md={12} className="details-equal-col">
                    <div className="details-col-container">
                        <DealDetail
                            label={isSupplyChain ? "Purchase Order Amount" : "Invoice Amount"}
                            value={formatCurrency(deal?.invoiceAmount)}
                        />
                        {isSupplyChain && (
                            <DealDetail
                                label="Material Cost"
                                value={formatCurrency(deal?.totalMaterialCost)}
                            />
                        )}
                        <DealDetail
                            label="Approved Amount"
                            value={formatCurrency(deal?.approvedAmount)}
                        />
                        <DealDetail
                            label="Funded Amount"
                            value={formatCurrency(deal?.fundedAmt)}
                        />
                        <DealDetail
                            label="Disbursed Amount"
                            value={formatCurrency(deal?.disbursedAmount)}
                        />
                        {/* <DealDetail
                            label="Enterprise Received Amount"
                            value={formatCurrency(deal?.enterpriseReceivedAmount)}
                        /> */}
                        <DealDetail
                            label="Amount Repaid to Investors"
                            value={formatCurrency(deal?.amountRepaidToInvestor)}
                        />
                        <DealDetail
                            label="Balance Amount"
                            value={formatCurrency(deal?.balanceAmount)}
                        />
                    </div>
                </Col>

                <Col
                    xs={0}
                    md={1}
                    style={{ display: "flex", justifyContent: "center" }}
                >
                    <Divider
                        type="vertical"
                        style={{
                            height: "100%",
                            borderLeft: "1px solid #CBD5E2",
                            margin: 0,
                        }}
                    />
                </Col>

                <Col xs={24} md={11} className="details-equal-col">
                    <div className="details-col-container">
                        <DealDetail
                            label="Expiry Date"
                            value={formatDate(deal?.expiryDate) || GLOBAL_CONSTANTS.NA}
                        />
                        <DealDetail
                            label="Approved Date"
                            value={formatDate(deal?.approvedDate) || GLOBAL_CONSTANTS.NA}
                        />
                        <DealDetail
                            label="Expected Repayment Date"
                            value={
                                formatDate(deal?.expectedRepaymentDate) || GLOBAL_CONSTANTS.NA
                            }
                        />
                        <DealDetail
                            label="Disbursed Date"
                            value={formatDate(deal?.disbursedDate) || GLOBAL_CONSTANTS.NA}
                        />
                        <DealDetail
                            label="Actual Repayment Date"
                            value={
                                formatDate(deal?.actualRepaymentDate) || GLOBAL_CONSTANTS.NA
                            }
                        />
                        <DealDetail
                            label="Settled Date"
                            value={formatDate(deal?.settledDate) || GLOBAL_CONSTANTS.NA}
                        />
                        <DealDetail label="Tenure" value={`${deal?.tenure} days`} />
                    </div>
                </Col>
            </Row>

            <Divider style={{ border: "1px solid #CBD5E2", margin: 0 }} />

            <Row className="details-row-container" gutter={[0, 0]}>
                <Col xs={24} md={12} className="details-equal-col">
                    <div className="details-col-container">
                        <DealDetail
                            label="Amount Repaid to Business"
                            value={formatCurrency(deal?.amountRepaidToBusiness)}
                        />
                        <DealDetail
                            label="Amount Repaid to Karncy"
                            value={formatCurrency(deal?.amountRepaidToKarncy)}
                        />
                    </div>
                </Col>

                <Col
                    xs={0}
                    md={1}
                    style={{ display: "flex", justifyContent: "center" }}
                >
                    <Divider
                        type="vertical"
                        style={{
                            height: "100%",
                            borderLeft: "1px solid #CBD5E2",
                            margin: 0,
                        }}
                    />
                </Col>

                <Col xs={24} md={11} className="details-equal-col">
                    <div className="details-col-container">
                        <DealDetail
                            label="Penalty Fees"
                            value={formatCurrency(deal?.penaltyFees)}
                        />
                        <DealDetail
                            label="Interest Rate"
                            value={`${(parseFloat(deal?.discountRate || 0) * 100).toFixed(2)}%`}
                        />
                    </div>
                </Col>
            </Row>

            {deal?.supplierAllocations?.length > 0 && (
                <>
                    <Divider style={{ border: "1px solid #CBD5E2", margin: 0 }} />
                    <div className="supplier-breakdown-container" style={{ padding: "20px" }}>
                        <Text className="supplier-breakdown-title" strong style={{ fontSize: 16 }}>
                            Supplier Breakdown
                        </Text>
                        <Table
                            dataSource={deal.supplierAllocations}
                            rowKey={(record) => record.supplierId || record.supplierName}
                            pagination={false}
                            tableLayout="fixed"
                            style={{ marginTop: 12 }}
                            columns={[
                                {
                                    title: "Supplier Name",
                                    dataIndex: "supplierName",
                                    key: "supplierName",
                                    align: "left",
                                    width: "55%",
                                    render: (val, record) => val || record.supplierId || GLOBAL_CONSTANTS.NA,
                                },
                                {
                                    title: "Allocated Amount",
                                    dataIndex: "allocatedAmount",
                                    key: "allocatedAmount",
                                    align: "right",
                                    width: "45%",
                                    render: (val) => formatCurrency(val),
                                },
                            ]}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default DealDetails;
